import { CalendarCheck, CalendarSync, MousePointerClick, Smile } from 'lucide-react'
import { ReactComponent as SignInWithGoogleButton } from 'src/assets/icons/web_light_rd_ctn.svg'
import { isKalamaBrandingEnabled } from 'src/modules/brand/utils/kalama-branding'
import { FormattedText, Icon, Typography } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { getBenefitsHeadingId } from './login.domain'
import classes from './login.module.scss'
import { useLogin } from './use-login'

export function Login() {
  const f = useLogin()

  if (!f.shouldShowContent) {
    return null
  }

  return (
    <div className={classes.root}>
      <section className={classes.hero}>
        <div className={classes.banner}>
          <Icon name="googleCalendar" className={classes.calendarIcon} />
          <Icon name="imessage" className={classes.messageIcon} />
          <Typography variant="span" className={classes.message}>
            <FormattedText id="login.exampleMessage" />
          </Typography>
        </div>
        <div className={classes.heroTitle}>
          <Typography variant="h1" className={classes.heroTitleMain}>
            <FormattedText id="login.hero.titleMain" />
            <span className={classes.heroTitleAccent}>
              <FormattedText id="login.hero.titleMainAccent" />
            </span>
          </Typography>
          <Typography variant="h2" element="span" className={classes.heroTitleTagline}>
            <FormattedText id="login.hero.titleTagline" />
          </Typography>
        </div>
        <button type="button" onClick={f.loginByCode} className={classes.ctaButton} aria-label="Sign in with Google">
          <SignInWithGoogleButton className={classes.ctaButtonSvg} aria-hidden />
        </button>
      </section>

      <section className={classes.benefits} aria-labelledby="benefits-heading">
        <Typography id="benefits-heading" variant="h2" className={classes.benefitsTitle}>
          <FormattedText id={getBenefitsHeadingId(isKalamaBrandingEnabled())} />
        </Typography>
        <Typography variant="body" className={classes.benefitsSubtitle}>
          <FormattedText id="login.hero.subtitle" />
        </Typography>
        <div className={classes.benefitsGrid}>
          <div className={classes.benefitCard}>
            <div className={classes.benefitIconWrap}>
              <Smile className={classes.benefitIcon} />
            </div>
            <Typography variant="h3" className={classes.benefitTitle}>
              <FormattedText id="login.benefits.stopNoShows.title" />
            </Typography>
            <Typography variant="body" className={classes.benefitDescription}>
              <FormattedText id="login.benefits.stopNoShows.description" />
            </Typography>
          </div>
          <div className={classes.benefitCard}>
            <div className={classes.benefitIconWrap}>
              <CalendarCheck className={classes.benefitIcon} />
            </div>
            <Typography variant="h3" className={classes.benefitTitle}>
              <FormattedText id="login.benefits.confirmations.title" />
            </Typography>
            <Typography variant="body" className={classes.benefitDescription}>
              <FormattedText id="login.benefits.confirmations.description" />
            </Typography>
          </div>
          <div className={classes.benefitCard}>
            <div className={classes.benefitIconWrap}>
              <Icon name="googleCalendar" className={classes.benefitIcon} />
            </div>
            <Typography variant="h3" className={classes.benefitTitle}>
              <FormattedText id="login.benefits.setAndForget.title" />
            </Typography>
            <Typography variant="body" className={classes.benefitDescription}>
              <FormattedText id="login.benefits.setAndForget.description" />
            </Typography>
          </div>
        </div>
      </section>

      <section className={classes.bookingBonus} aria-labelledby="booking-heading">
        <div className={classes.bookingCard}>
          <span className={classes.bookingRibbon} aria-hidden>
            <FormattedText id="login.booking.included" />
          </span>
          <Typography id="booking-heading" variant="h2" className={classes.bookingTitle}>
            <FormattedText id="login.booking.title" />
          </Typography>
          <Typography variant="body" className={classes.bookingSubtitle}>
            <FormattedText id="login.booking.subtitle" />
          </Typography>
          <ul className={classes.bookingList}>
            <li>
              <div className={classes.bookingItemIconWrap}>
                <CalendarSync className={classes.bookingListIcon} />
              </div>
              <div className={classes.bookingItemContent}>
                <Typography variant="h3" className={classes.bookingItemTitle}>
                  <FormattedText id="login.booking.calendarSync.title" />
                </Typography>
                <Typography variant="body" className={classes.bookingItemDescription}>
                  <FormattedText id="login.booking.calendarSync.description" />
                </Typography>
              </div>
            </li>
            <li>
              <div className={classes.bookingItemIconWrap}>
                <Icon name="clock" className={classes.bookingListIcon} />
              </div>
              <div className={classes.bookingItemContent}>
                <Typography variant="h3" className={classes.bookingItemTitle}>
                  <FormattedText id="login.booking.smartScheduling.title" />
                </Typography>
                <Typography variant="body" className={classes.bookingItemDescription}>
                  <FormattedText id="login.booking.smartScheduling.description" />
                </Typography>
              </div>
            </li>
            <li>
              <div className={classes.bookingItemIconWrap}>
                <MousePointerClick className={classes.bookingListIcon} />
              </div>
              <div className={classes.bookingItemContent}>
                <Typography variant="h3" className={classes.bookingItemTitle}>
                  <FormattedText id="login.booking.easyBooking.title" />
                </Typography>
                <Typography variant="body" className={classes.bookingItemDescription}>
                  <FormattedText id="login.booking.easyBooking.description" />
                </Typography>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <footer className={classes.footer}>
        <nav className={classes.footerNav} aria-label="Footer">
          <a href="https://smsreminder.app/#terms" target="_blank" rel="noreferrer" className={classes.footerLink}>
            <FormattedText id="login.footer.terms" />
          </a>
          <a href="https://smsreminder.app/#privacy" target="_blank" rel="noreferrer" className={classes.footerLink}>
            <FormattedText id="login.footer.privacy" />
          </a>
          <a href={routeNames.support.path} className={classes.footerLink}>
            <FormattedText id="login.footer.support" />
          </a>
          <a
            href="https://smsreminder.app/#get-started"
            target="_blank"
            rel="noreferrer"
            className={classes.footerLink}
          >
            <FormattedText id="login.footer.getStarted" />
          </a>
        </nav>
      </footer>
    </div>
  )
}
